import React, { memo, useEffect, useState, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Printer, Languages, FileText, Loader2, Share2, ChevronDown, ChevronUp } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { adminGetClinic, getDoctorInfo, getPatient, getSettings } from "@/lib/api";
import { toast } from "@/components/ui/use-toast";
import { PrescriptionContent } from "@/components/Print/PrescriptionContent";
import { InvoiceContent } from "@/components/Print/InvoiceContent";
import type { InvoiceData } from "@/components/Print/InvoiceContent";
import { DischargeSummaryContent } from "@/components/Print/DischargeSummaryContent";
import ErrorBoundary from '@/components/ErrorBoundary';
import { useAuth } from "@/context/AuthContext.tsx";
import { DateUtil } from "@/utils/dateUtil.ts";

// ── Types ────────────────────────────────────────────────────────────────────

export type PrintType     = 'prescription' | 'invoice' | 'discharge';
export type PrintLanguage = 'en' | 'hi' | 'mr';

export interface HeaderConfig {
  mode: 'auto' | 'none' | 'custom';
  showName:    boolean;
  showAddress: boolean;
  showPhone:   boolean;
  topSpaceMm:  number;
  customText:  string;
}

const DEFAULT_HEADER_CONFIG: HeaderConfig = {
  mode: 'auto', showName: true, showAddress: true, showPhone: true,
  topSpaceMm: 20, customText: '',
};

function loadHeaderConfig(): HeaderConfig {
  try {
    const raw = localStorage.getItem('printHeaderConfig');
    if (raw) return { ...DEFAULT_HEADER_CONFIG, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_HEADER_CONFIG };
}

function saveHeaderConfig(cfg: HeaderConfig) {
  try { localStorage.setItem('printHeaderConfig', JSON.stringify(cfg)); } catch { /* ignore */ }
}

interface PrescriptionData {
  id?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  patient_name?: string;
  date?: string;
  [key: string]: unknown;
}

interface DischargeData {
  id?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  patient_name?: string;
  admission_number?: string;
  admission_date?: string;
  discharge_date?: string;
  status?: string;
  [key: string]: unknown;
}

interface ClinicInfo  { name?: string; address?: string; phone?: string; email?: string; gstin?: string; [key: string]: unknown; }
interface DoctorInfo  { name?: string; specialization?: string; registration_no?: string; [key: string]: unknown; }
interface PatientInfo { name?: string; phone?: string; email?: string; [key: string]: unknown; }
interface Settings    { profile?: DoctorInfo; [key: string]: unknown; }

interface UnifiedPrintModalProps {
  open: boolean;
  onClose: () => void;
  type: PrintType;
  data: PrescriptionData | InvoiceData | DischargeData | null;
  title?: string;
}

// ── Translations ─────────────────────────────────────────────────────────────

const printTranslations = {
  en: {
    date: "Date", time: "Time", phone: "Phone", email: "Email",
    total: "Total", generated: "Generated on", information: "Information", details: "Details",
    morning: "Morning", afternoon: "Afternoon", evening: "Evening", night: "Night",
    beforeFood: "Before Food", afterFood: "After Food", withFood: "With Food",
    emptyStomach: "Empty Stomach", days: "days", weeks: "weeks", months: "months",
    asDirected: "As Directed", investigations: "Investigations:", nextVisit: "Next Visit:", advice: "Advice:",
    afterBreakfast: "After Breakfast", beforeBreakfast: "Before Breakfast", withBreakfast: "With Breakfast",
    afterLunch: "After Lunch", beforeLunch: "Before Lunch", withLunch: "With Lunch",
    afterDinner: "After Dinner", beforeDinner: "Before Dinner", withDinner: "With Dinner",
    bedtime: "At Bedtime", sos: "SOS",
    invoice: "Invoice", invoiceNumber: "Invoice Number", name: "Name", doctor: "Doctor",
    patient: "Patient", service: "Service", category: "Category", quantity: "Qty",
    unitPrice: "Unit Price", totalAmount: "Total Amount", amountPaid: "Amount Paid",
    balanceDue: "Balance Due", paymentMethod: "Payment Method", status: "Status",
    subtotal: "Subtotal", services: "Services", charges: "Charges",
  },
  hi: {
    date: "दिनांक", time: "समय", phone: "फोन", email: "ईमेल",
    total: "कुल", generated: "उत्पन्न", information: "जानकारी", details: "विवरण",
    morning: "सुबह", afternoon: "दोपहर", evening: "शाम", night: "रात",
    beforeFood: "खाना पहले", afterFood: "खाना बाद", withFood: "खाना के साथ",
    emptyStomach: "खाली पेट", days: "दिन", weeks: "सप्ताह", months: "महीने",
    asDirected: "निर्देशानुसार", investigations: "जांच चाहिये:", nextVisit: "अगली मुलाकात:", advice: "सलाह:",
    afterBreakfast: "नाश्ते के बाद", beforeBreakfast: "नाश्ते से पहले", withBreakfast: "नाश्ते के साथ",
    afterLunch: "दोपहर के भोजन के बाद", beforeLunch: "दोपहर के भोजन से पहले", withLunch: "दोपहर के भोजन के साथ",
    afterDinner: "रात के खाने के बाद", beforeDinner: "रात के खाने से पहले", withDinner: "रात के खाने के साथ",
    atBedtime: "सोने से पहले", sos: "ज़रूरत अनुसार",
    invoice: "बिल", invoiceNumber: "बिल संख्या", name: "नाम", doctor: "डॉक्टर",
    patient: "मरीज़", service: "सेवा", category: "श्रेणी", quantity: "मात्रा",
    unitPrice: "दर", totalAmount: "कुल राशि", amountPaid: "भुगतान", balanceDue: "बकाया",
    paymentMethod: "भुगतान विधि", status: "स्थिति", subtotal: "उप-योग",
    services: "सेवाएं", charges: "शुल्क",
  },
  mr: {
    date: "दिनांक", time: "वेळ", phone: "फोन", email: "ईमेल",
    total: "एकूण", generated: "तयार केले", information: "माहिती", details: "तपशील",
    morning: "सकाळ", afternoon: "दुपार", evening: "संध्याकाळ", night: "रात्री",
    beforeFood: "जेवणापूर्वी", afterFood: "जेवणानंतर", withFood: "जेवणासह",
    emptyStomach: "रिकाम्या पोटी", days: "दिवस", weeks: "आठवडे", months: "महिने",
    asDirected: "निर्देशानुसार", investigations: "तपासणी आवश्यक:", nextVisit: "पुढची भेट:", advice: "सल्ला:",
    afterBreakfast: "नाश्त्यानंतर", beforeBreakfast: "नाश्त्यापूर्वी", withBreakfast: "नाश्त्यासह",
    afterLunch: "दुपारच्या जेवणानंतर", beforeLunch: "दुपारच्या जेवणापूर्वी", withLunch: "दुपारच्या जेवणासह",
    afterDinner: "रात्रीच्या जेवणानंतर", beforeDinner: "रात्रीच्या जेवणापूर्वी", withDinner: "रात्रीच्या जेवणासह",
    bedtime: "झोपण्यापूर्वी", sos: "गरजेनुसार",
    invoice: "बिल", invoiceNumber: "बिल क्रमांक", name: "नाव", doctor: "डॉक्टर",
    patient: "रुग्ण", service: "सेवा", category: "प्रकार", quantity: "संख्या",
    unitPrice: "दर", totalAmount: "एकूण रक्कम", amountPaid: "भरलेली रक्कम",
    balanceDue: "उरलेली रक्कम", paymentMethod: "पेमेंट पद्धत", status: "स्थिती",
    subtotal: "उप-एकूण", services: "सेवा", charges: "शुल्क",
  },
};

// ── Iframe HTML builder ───────────────────────────────────────────────────────

function buildIframeHtml(
  type: PrintType,
  data: PrescriptionData | InvoiceData | DischargeData,
  t: Record<string, string>,
  language: PrintLanguage,
  clinicInfo: ClinicInfo | null,
  doctorInfo: DoctorInfo | null,
  patientInfo: PatientInfo | null,
  settings: Settings | null,
  currentUser: { name?: string } | null,
  headerConfig: HeaderConfig,
): string {
  const resolvedDoctor = settings?.profile ?? doctorInfo;

  let body: string;
  if (type === 'prescription') {
    body = renderToStaticMarkup(
      React.createElement(PrescriptionContent, {
        data, translations: t, language,
        clinicInfo, doctorInfo: resolvedDoctor, patientInfo, settings, currentUser, headerConfig,
      })
    );
  } else if (type === 'discharge') {
    body = renderToStaticMarkup(
      React.createElement(DischargeSummaryContent, {
        data, translations: t, language,
        clinicInfo, doctorInfo: resolvedDoctor, patientInfo, settings, currentUser, headerConfig,
      })
    );
  } else {
    body = renderToStaticMarkup(
      React.createElement(InvoiceContent, {
        data: data as InvoiceData, translations: t, language,
        clinicInfo, doctorInfo: resolvedDoctor, patientInfo, settings, currentUser, headerConfig,
      })
    );
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<style>
  *, *::before, *::after { box-sizing: border-box; }
  html, body {
    font-family: Arial, 'Helvetica Neue', sans-serif;
    font-size: 11px;
    line-height: 1.4;
    color: #000;
    background: #fff;
    margin: 0;
    padding: 12mm 15mm;
  }
  @page { size: A4 portrait; margin: 0; }
  @media print {
    html, body { padding: 10mm; }
  }
</style>
</head>
<body>${body}</body>
</html>`;
}

// ── Component ────────────────────────────────────────────────────────────────

function UnifiedPrintModal({ open, onClose, type, data, title }: Readonly<UnifiedPrintModalProps>) {

  const { currentUser } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [printLanguage, setPrintLanguage]   = useState<PrintLanguage>('en');
  const [autoOpenPDF, setAutoOpenPDF]       = useState(true);
  const [isPrinting, setIsPrinting]         = useState(false);
  const [isSavingPDF, setIsSavingPDF]       = useState(false);
  const [isLoading, setIsLoading]           = useState(false);
  const [headerConfig, setHeaderConfig]     = useState<HeaderConfig>(loadHeaderConfig);
  const [headerExpanded, setHeaderExpanded] = useState(false);

  const updateHeaderConfig = (patch: Partial<HeaderConfig>) => {
    setHeaderConfig(prev => {
      const next = { ...prev, ...patch };
      saveHeaderConfig(next);
      return next;
    });
  };

  const [clinicInfo,  setClinicInfo]  = useState<ClinicInfo  | null>(null);
  const [doctorInfo,  setDoctorInfo]  = useState<DoctorInfo  | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [settings,    setSettings]    = useState<Settings    | null>(null);

  // Load clinic / doctor / patient data when modal opens
  useEffect(() => {
    if (!open) return;

    setClinicInfo(null);
    setDoctorInfo(null);
    setPatientInfo(null);
    setSettings(null);
    setIsLoading(true);

    if (!data) { setIsLoading(false); return; }

    const d = data as PrescriptionData;
    if (!d.doctor_id || !d.clinic_id) { setIsLoading(false); return; }

    (async () => {
      try {
        const settingsData = await getSettings(d.doctor_id, d.clinic_id);
        setSettings(settingsData);
        if (!settingsData?.profile?.name) {
          const doctor = await getDoctorInfo(d.doctor_id);
          setDoctorInfo(doctor);
        }
        const clinic = await adminGetClinic();
        setClinicInfo(clinic);
        if (d.patient_id) {
          const patient = await getPatient(d.patient_id);
          setPatientInfo(patient);
        }
      } catch {
        toast({ title: "Data Loading Error", description: "Some information may not display correctly", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [open, data]);

  // Write HTML into the iframe whenever content or language changes
  useEffect(() => {
    if (isLoading || !data || !iframeRef.current) return;
    const t = printTranslations[printLanguage];
    const html = buildIframeHtml(
      type, data, t, printLanguage,
      clinicInfo, doctorInfo, patientInfo, settings, currentUser ?? null,
      headerConfig,
    );
    const doc = iframeRef.current.contentDocument ?? iframeRef.current.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
  }, [isLoading, data, type, printLanguage, clinicInfo, doctorInfo, patientInfo, settings, currentUser, headerConfig]);

  const getDocDate = () => {
    if (!data) return new Date();
    const inv = data as InvoiceData;
    const rx  = data as PrescriptionData;
    const dis = data as DischargeData;
    if (type === 'discharge') return new Date(dis.discharge_date ?? dis.admission_date ?? new Date());
    return new Date(inv.invoice_date ?? rx.date ?? new Date());
  };

  const generatePrintTitle = () => {
    const date        = getDocDate().toLocaleDateString();
    const patientName = patientInfo?.name ?? (data as PrescriptionData)?.patient_name ?? 'Patient';
    const doctor      = settings?.profile?.name ?? doctorInfo?.name ?? 'Doctor';
    if (type === 'prescription') return `Prescription - ${patientName} - ${date} - Dr. ${doctor}`;
    if (type === 'discharge')    return `Discharge Summary - ${patientName} - ${date}`;
    const invoiceRef = (data as InvoiceData)?.invoice_number ?? date;
    return `Invoice ${invoiceRef} - ${patientName} - ${date}`;
  };

  const generateFilename = () => {
    const date    = getDocDate();
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
    const clean   = (s: string) => (s || '').replace(/[^a-zA-Z0-9]/g, '_');
    const patient = clean(patientInfo?.name ?? (data as PrescriptionData)?.patient_name ?? 'Patient');
    const doctor  = clean(settings?.profile?.name ?? doctorInfo?.name ?? 'Doctor');
    const clinic  = clean(clinicInfo?.name ?? 'Clinic');
    if (type === 'prescription') return `Prescription_${patient}_${dateStr}_${timeStr}_Dr${doctor}_${clinic}.pdf`;
    if (type === 'discharge')    return `Discharge_Summary_${patient}_${dateStr}_${clinic}.pdf`;
    const ref = clean((data as InvoiceData)?.invoice_number ?? dateStr);
    return `Invoice_${ref}_${patient}_${dateStr}_Dr${doctor}_${clinic}.pdf`;
  };

  const handlePrint = async () => {
    if (isPrinting || isLoading) return;
    setIsPrinting(true);
    try {
      if (globalThis.electronAPI) {
        const result = await globalThis.electronAPI.printPrescription({
          paperSize: 'A4', margins: { top: 10, bottom: 10, left: 10, right: 10 },
          silent: false, scale: 100, jobName: generatePrintTitle(),
        });
        if (!result.success) throw new Error(result.error);
        toast({ title: "Print Successful", variant: "default" });
      } else {
        iframeRef.current?.contentWindow?.print();
      }
    } catch {
      iframeRef.current?.contentWindow?.print();
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSavePDF = async () => {
    if (!globalThis.electronAPI || isSavingPDF) return;
    setIsSavingPDF(true);
    try {
      const result = await globalThis.electronAPI.printToPDF({
        paperSize: 'A4', marginsType: 2, save: true,
        filename: generateFilename(), autoOpen: autoOpenPDF,
        metadata: {
          title:       generatePrintTitle(),
          author:      `Dr. ${settings?.profile?.name ?? doctorInfo?.name ?? 'Doctor'}`,
          subject:     `Medical ${type === 'prescription' ? 'Prescription' : type === 'discharge' ? 'Discharge Summary' : 'Invoice'}`,
          creator:     `${clinicInfo?.name ?? 'Clinic'} - OneCheckUp`,
          creationDate: getDocDate().toISOString(),
          modDate:     new Date().toISOString(),
        },
      });
      if (!result.success) throw new Error(result.error);
      toast({ title: "PDF Saved", description: result.path ?? 'Saved successfully', variant: "default" });
    } catch {
      toast({ title: "PDF Export Error", description: "Failed to save PDF. Please try again.", variant: "destructive" });
    } finally {
      setIsSavingPDF(false);
    }
  };

  const handleShare = async () => {
    const patientName = patientInfo?.name ?? (data as PrescriptionData)?.patient_name ?? 'Patient';
    const clinic      = clinicInfo?.name ?? 'Clinic';
    const date        = getDocDate();
    let shareText: string;
    if (type === 'invoice') {
      const inv = data as InvoiceData;
      shareText = `${clinic}\nInvoice ${inv?.invoice_number ?? ''}\nPatient: ${patientName}\nAmount: ₹${Number(inv?.total_amount ?? 0).toFixed(2)}`;
    } else if (type === 'discharge') {
      shareText = `${clinic}\nDischarge Summary for ${patientName}\nDate: ${DateUtil.formatDateForType(date.toISOString(), 'date-time')}`;
    } else {
      shareText = `${clinic}\nPrescription for ${patientName}\nDate: ${DateUtil.formatDateForType(date.toISOString(), 'date-time')}`;
    }
    try {
      if (navigator.share) {
        await navigator.share({ title: generatePrintTitle(), text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast({ title: "Copied to clipboard", description: "Paste into WhatsApp, SMS, or email to share", variant: "default" });
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        toast({ title: "Share failed", description: "Could not share. Try copying manually.", variant: "destructive" });
      }
    }
  };

  const typeLabel = type === 'prescription' ? 'Prescription' : type === 'discharge' ? 'Discharge Summary' : 'Invoice';

  return (
    <ErrorBoundary>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="max-w-[96vw] max-h-[96vh] w-full h-full flex p-0" showClose={false}>

          {/* ── Preview iframe — left ───────────────────────────────────── */}
          <div className="flex-1 flex flex-col border-r border-border overflow-hidden bg-muted/10">
            <div className="bg-muted/40 px-4 py-2 border-b flex-shrink-0">
              <p className="text-sm font-medium text-muted-foreground">
                {title || `${typeLabel} Preview`}
              </p>
            </div>

            <div className="flex-1 overflow-auto flex justify-center items-start p-6 bg-muted/20">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                  <Loader2 className="w-7 h-7 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading preview…</p>
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  title="print-preview"
                  style={{
                    width: 'min(210mm, calc(100vw - 360px))',
                    minHeight: '297mm',
                    maxWidth: '794px',
                    border: 'none',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
                    backgroundColor: '#fff',
                    display: 'block',
                  }}
                />
              )}
            </div>
          </div>

          {/* ── Controls — right ────────────────────────────────────────── */}
          <div className="w-72 bg-background p-4 space-y-4 overflow-y-auto flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-foreground text-sm">Print Settings</h2>
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Header Settings */}
            <div className="space-y-2 border border-border rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/50 transition-colors"
                onClick={() => setHeaderExpanded(e => !e)}
              >
                <span>Header Settings</span>
                {headerExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {headerExpanded && (
                <div className="px-3 pb-3 space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Header mode</Label>
                    <Select value={headerConfig.mode} onValueChange={(v) => updateHeaderConfig({ mode: v as HeaderConfig['mode'] })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (from database)</SelectItem>
                        <SelectItem value="none">None (pre-printed letterhead)</SelectItem>
                        <SelectItem value="custom">Custom text</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {headerConfig.mode === 'auto' && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Show fields</Label>
                      {[
                        { id: 'showName',    label: 'Clinic name' },
                        { id: 'showAddress', label: 'Address' },
                        { id: 'showPhone',   label: 'Phone' },
                      ].map(({ id, label }) => (
                        <div key={id} className="flex items-center gap-2">
                          <Checkbox
                            id={id}
                            checked={headerConfig[id as keyof HeaderConfig] as boolean}
                            onCheckedChange={(c) => updateHeaderConfig({ [id]: c === true })}
                          />
                          <Label htmlFor={id} className="text-xs font-normal cursor-pointer">{label}</Label>
                        </div>
                      ))}
                    </div>
                  )}

                  {headerConfig.mode === 'none' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Space reserved for letterhead (mm)</Label>
                      <input
                        type="number"
                        min={0}
                        max={80}
                        value={headerConfig.topSpaceMm}
                        onChange={(e) => updateHeaderConfig({ topSpaceMm: Number(e.target.value) })}
                        className="w-full h-8 rounded-md border border-input bg-background px-3 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                  )}

                  {headerConfig.mode === 'custom' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Header text</Label>
                      <Textarea
                        rows={4}
                        placeholder={"Clinic name\nAddress line 1\nPhone: 9999999999"}
                        value={headerConfig.customText}
                        onChange={(e) => updateHeaderConfig({ customText: e.target.value })}
                        className="text-xs resize-none"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Language */}
            {type !== 'discharge' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Languages className="w-4 h-4" />
                  Print Language
                </Label>
                <Select value={printLanguage} onValueChange={(v) => setPrintLanguage(v as PrintLanguage)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">हिंदी (Hindi)</SelectItem>
                    <SelectItem value="mr">मराठी (Marathi)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* PDF options — Electron only */}
            {globalThis.electronAPI && (
              <div className="space-y-2 p-3 bg-muted/30 rounded-lg border border-border">
                <div className="text-sm font-medium mb-2">PDF Options</div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="autoOpen"
                    checked={autoOpenPDF}
                    onCheckedChange={(c) => setAutoOpenPDF(c === true)}
                  />
                  <Label htmlFor="autoOpen" className="text-sm font-normal cursor-pointer">
                    Auto-open PDF after saving
                  </Label>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-2">
              <Button onClick={handlePrint} disabled={isPrinting || isLoading} className="w-full">
                {isPrinting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Printing…</>
                  : <><Printer className="w-4 h-4 mr-2" />Print {typeLabel}</>}
              </Button>

              <Button onClick={handleShare} variant="outline" className="w-full">
                <Share2 className="w-4 h-4 mr-2" />Share
              </Button>

              {globalThis.electronAPI && (
                <Button onClick={handleSavePDF} disabled={isSavingPDF || isLoading} variant="outline" className="w-full text-primary border-primary/30 hover:bg-primary/5">
                  {isSavingPDF
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                    : <><FileText className="w-4 h-4 mr-2" />Save as PDF</>}
                </Button>
              )}

              <Button variant="outline" onClick={onClose} className="w-full">
                <X className="w-4 h-4 mr-2" />Cancel
              </Button>
            </div>

          </div>

        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
}

export default memo(UnifiedPrintModal);
